import { createAction } from "redux-actions";
import { takeLatest, put, select, call } from "redux-saga/effects";
import dayjs from "dayjs";
import { api } from "../api";
import { getCommonApiParams } from "../../../../utils/redux-helpers";
import { API_DATE_FORMAT } from "../../../../app_constants";
import { VIEW_OPTIONS } from "../constants";

export const SET_LOADING = "saga/freezeLifecycle/SET_LOADING";
export const SET_UPDATES_LOADING = "saga/freezeLifecycle/SET_UPDATES_LOADING";
export const SET_FILTERS_LOADING = "saga/freezeLifecycle/SET_FILTERS_LOADING";
export const SET_FILTERS_DIRTY = "saga/freezeLifecycle/SET_FILTERS_DIRTY";
export const SET_MODULE_VIEW = "saga/freezeLifecycle/SET_MODULE_VIEW";
export const SET_DATES = "saga/freezeLifecycle/SET_DATES";

export const FETCH_CURRENT_USER = "saga/freezeLifecycle/FETCH_CURRENT_USER";
export const FETCH_CURRENT_USER_SUCCESS = "saga/freezeLifecycle/FETCH_CURRENT_USER_SUCCESS";
export const FETCH_FILTERS_SUCCESS = "saga/freezeLifecycle/FETCH_FILTERS_SUCCESS";
export const FETCH_DATA_SUCCESS = "saga/freezeLifecycle/FETCH_DATA_SUCCESS";

export const UPDATE_FILTER = "saga/freezeLifecycle/UPDATE_FILTER";
export const UPDATE_DATES = "saga/freezeLifecycle/UPDATE_DATES";
export const UPDATE_CUSTOM_RANGE = "saga/freezeLifecycle/UPDATE_CUSTOM_RANGE";
export const UPDATE_DATA = "saga/freezeLifecycle/UPDATE_DATA";
export const UPDATE_MODULE_VIEW = "saga/freezeLifecycle/UPDATE_MODULE_VIEW";

export const RESET_FILTER = "saga/freezeLifecycle/RESET_FILTER";
export const RESET_SELECTED_FILTERS = "saga/freezeLifecycle/RESET_SELECTED_FILTERS";

export const actionCreators = {
  fetchCurrentUser: createAction(FETCH_CURRENT_USER),
  updateFilter: createAction(UPDATE_FILTER),
  updateDates: createAction(UPDATE_DATES),
  updateCustomRange: createAction(UPDATE_CUSTOM_RANGE),
  updateData: createAction(UPDATE_DATA),
  updateModuleView: createAction(UPDATE_MODULE_VIEW),
  resetFilters: createAction(RESET_FILTER)
};

export default function* watcher() {
  yield takeLatest(FETCH_CURRENT_USER, fetchCurrentUserData);
  yield takeLatest(UPDATE_DATA, updateData);
  yield takeLatest(RESET_FILTER, resetFilters);
  yield takeLatest(UPDATE_MODULE_VIEW, updateModuleView);
}

export function* fetchCurrentUserData(data) {
  yield put({
    type: FETCH_CURRENT_USER_SUCCESS,
    payload: data.payload
  });

  yield put({ type: SET_LOADING, payload: true });

  yield setDates();

  try {
    yield fetchFilters();
    yield fetchData();

    yield put({ type: SET_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* fetchFilters() {
  const commonParams = yield getCommonApiParams("commonReducer", "commonProps");
  const dateParams = yield getDateParams();
  const { moduleView } = yield select((state) => state.freezeLifecycle.selectedFilters);

  yield put({ type: SET_FILTERS_LOADING, payload: true });

  try {
    const { data } = yield call(api.getFilters, { ...commonParams, ...dateParams }, moduleView.id);

    yield put({
      type: FETCH_FILTERS_SUCCESS,
      payload: data
    });
  } catch (error) {
    console.error(error);
  } finally {
    yield put({ type: SET_FILTERS_LOADING, payload: false });
  }
}

function* updateData() {
  yield put({ type: SET_UPDATES_LOADING, payload: true });

  try {
    yield put({ type: SET_FILTERS_DIRTY, payload: false });

    yield fetchData();

    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* resetFilters() {
  yield put({ type: SET_UPDATES_LOADING, payload: true });

  yield put({ type: RESET_SELECTED_FILTERS });
  yield setDates();

  try {
    yield fetchFilters();
    yield fetchData();

    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

function* updateModuleView({ payload }) {
  yield put({ type: SET_MODULE_VIEW, payload });
  yield put({ type: RESET_SELECTED_FILTERS });
  yield setDates();

  yield put({ type: SET_UPDATES_LOADING, payload: true });

  try {
    yield fetchFilters();
    yield fetchData();

    yield put({ type: SET_UPDATES_LOADING, payload: false });
  } catch (error) {
    console.error(error);
  }
}

// Currently Frozen has no user-editable date — only Freeze History needs a computed default.
function* setDates() {
  const moduleView = yield select((state) => state.freezeLifecycle.selectedFilters.moduleView);
  if (moduleView.id !== VIEW_OPTIONS[1].id) return;

  const endDate = yield select((state) => state.freezeLifecycle.currentUser.calendarEndDate);
  const startDate = dayjs(endDate).startOf("month").format(API_DATE_FORMAT);

  yield put({ type: SET_DATES, payload: { startDate, endDate } });
}

// Currently Frozen is a point-in-time snapshot; Freeze History scopes by the selected start/end range.
function* getDateParams() {
  const { calendarEndDate } = yield select((state) => state.freezeLifecycle.currentUser);
  const { moduleView, startDate, endDate } = yield select(
    (state) => state.freezeLifecycle.selectedFilters
  );

  return moduleView.id === VIEW_OPTIONS[0].id
    ? { date: calendarEndDate }
    : { start_date: startDate, end_date: endDate };
}

function* getApiParams() {
  const commonParams = yield getCommonApiParams("commonReducer", "commonProps");
  const dateParams = yield getDateParams();
  const { membershipTypes, durationBuckets } = yield select(
    (state) => state.freezeLifecycle.selectedFilters
  );

  return {
    ...commonParams,
    ...dateParams,
    membership_type_ids: membershipTypes.map((type) => type.id),
    duration_buckets: durationBuckets.map((bucket) => bucket.id)
  };
}

function* fetchData() {
  const params = yield getApiParams();
  const { moduleView } = yield select((state) => state.freezeLifecycle.selectedFilters);

  try {
    const { data } = yield call(api.getTable, params, moduleView.id);

    yield put({
      type: FETCH_DATA_SUCCESS,
      payload: data
    });
  } catch (error) {
    console.error(error);
  }
}
